// Compares what the learner said — as transcribed by the browser's own speech
// recogniser — with the target sentence. Port of `tutor/PronunciationScorer.kt`.
//
// The Android build noted that no audio leaves the device. On the web that
// depends on the browser: Chrome's Web Speech API sends audio to Google's
// servers, while Safari and Edge can recognise on-device. The scoring itself,
// below, is always local.

export type WordStatus = 'CORRECT' | 'CLOSE' | 'MISSED';

/** How one expected word came out. */
export interface WordScore {
  expected: string;
  status: WordStatus;
}

export interface PronunciationResult {
  score: number;
  words: WordScore[];
  heard: string;
  feedback: string;
}

const CLOSE_THRESHOLD = 0.7;

export function score(target: string, heard: string): PronunciationResult {
  const expected = tokenize(target);
  const said = tokenize(heard);
  if (expected.length === 0) {
    return { score: 0, words: [], heard, feedback: 'Nothing to compare.' };
  }

  const matched = longestCommonSubsequenceFlags(expected, said);
  const words: WordScore[] = expected.map((word, index) => {
    let status: WordStatus;
    if (matched[index]) {
      status = 'CORRECT';
    } else if (said.some((s) => similarity(s, word) >= CLOSE_THRESHOLD)) {
      status = 'CLOSE';
    } else {
      status = 'MISSED';
    }
    return { expected: word, status };
  });

  const points = words.reduce(
    (sum, w) => sum + (w.status === 'CORRECT' ? 1 : w.status === 'CLOSE' ? 0.5 : 0),
    0,
  );
  const percent = Math.min(100, Math.max(0, Math.trunc((points / expected.length) * 100)));
  return { score: percent, words, heard, feedback: feedbackFor(percent, words) };
}

function feedbackFor(percent: number, words: WordScore[]): string {
  const problems = words.filter((w) => w.status !== 'CORRECT').map((w) => w.expected);
  if (percent >= 95) return 'Outstanding! That was clear and accurate. 🌟';
  if (percent >= 80) return `Very good! Just polish: ${problems.slice(0, 3).join(', ')}.`;
  if (percent >= 60) return `Good effort. Slow down on: ${problems.slice(0, 3).join(', ')}.`;
  if (percent > 0) return 'Keep going — try again more slowly, word by word.';
  return "I didn't catch that. Move closer to the microphone and try again.";
}

function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9' ]/g, ' ')
    .split(/\s+/)
    .filter((w) => w.trim() !== '');
}

/** Marks which entries of `expected` appear, in order, inside `said`. */
function longestCommonSubsequenceFlags(expected: string[], said: string[]): boolean[] {
  const n = expected.length;
  const m = said.length;
  const table: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      table[i]![j] =
        expected[i] === said[j]
          ? table[i + 1]![j + 1]! + 1
          : Math.max(table[i + 1]![j]!, table[i]![j + 1]!);
    }
  }
  const flags = new Array<boolean>(n).fill(false);
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (expected[i] === said[j]) {
      flags[i] = true;
      i++;
      j++;
    } else if (table[i + 1]![j]! >= table[i]![j + 1]!) {
      i++;
    } else {
      j++;
    }
  }
  return flags;
}

/** 0..1 similarity from Levenshtein distance, for near-misses like "tree"/"three". */
export function similarity(a: string, b: string): number {
  if (a === b) return 1;
  const distance = levenshtein(a, b);
  const longest = Math.max(a.length, b.length);
  return longest === 0 ? 1 : 1 - distance / longest;
}

function levenshtein(a: string, b: string): number {
  let previous = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const current = new Array<number>(b.length + 1).fill(0);
    current[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
      current[j] = Math.min(previous[j]! + 1, current[j - 1]! + 1, previous[j - 1]! + cost);
    }
    previous = current;
  }
  return previous[b.length]!;
}
